import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/api";
import { Star32Icon, XIcon } from "@/components/icons";
import { LeaveReviewModal, type ReviewMediaItem } from "@/components/shop/leave-review-modal";
import { cn } from "@/lib/utils";
import { useProfile } from "@/store";
const DISMISS_KEY = "osmix-rate-dismissed";
const readDismissed = (): string[] => {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    return raw ? JSON.parse(raw) as string[] : [];
  } catch {
    return [];
  }
};

/*
 * «Когда товар куплен» (кадр 3869:20098): у купленного и ещё не оценённого
 * товара внизу экрана полоса «Оцените товар» с пятью звёздами 36 и крестиком.
 * Нажатие на звезду открывает «Оставить отзыв» (кадр 3870:20529) с этой
 * оценкой. Источник — тот же список «Ждут отзыва», что в профиле (доставленные
 * заказы без отзыва), поэтому отзыв привязывается к заказу. Крестик прячет
 * полосу до конца сессии.
 */
export function RatePurchasedBar(t0) {
  const $ = _c(60);
  const {
    productId
  } = t0;
  const {
    t
  } = useTranslation();
  const qc = useQueryClient();
  const token = useProfile(_temp);
  const userId = useProfile(_temp2);
  let t1;
  if ($[0] !== userId) {
    t1 = ["buyer", userId, "reviews", "pending"];
    $[0] = userId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = Boolean(token);
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: _temp3,
      enabled: t2,
      staleTime: 60000
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const pending = useQuery(t3);
  let t4;
  if ($[5] !== pending.data) {
    t4 = pending.data ?? [];
    $[5] = pending.data;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== productId || $[8] !== t4) {
    let t6;
    if ($[10] !== productId) {
      t6 = row => row.productId === productId;
      $[10] = productId;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    t5 = t4.find(t6);
    $[7] = productId;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const item = t5;
  let t6;
  if ($[12] !== productId) {
    t6 = () => readDismissed().includes(productId);
    $[12] = productId;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const [dismissed, setDismissed] = useState(t6);
  const [hover, setHover] = useState(0);
  const [stars, setStars] = useState(0);
  if (!token || !item || dismissed) {
    return null;
  }
  let t7;
  if ($[14] !== productId) {
    t7 = () => {
      setDismissed(true);
      try {
        sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...readDismissed(), productId]));
      } catch {}
    };
    $[14] = productId;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const dismiss = t7;
  let t8;
  if ($[16] !== item.orderId || $[17] !== item.productId || $[18] !== productId || $[19] !== qc || $[20] !== t || $[21] !== userId) {
    t8 = async input => {
      await api.cabinet.createReview({
        productId: item.productId,
        orderId: item.orderId,
        rating: input.rating,
        text: input.text || undefined,
        anonymous: input.anonymous,
        media: input.media
      });
      toast.success(t("product.reviewThanks", {
        defaultValue: "\u0421\u043F\u0430\u0441\u0438\u0431\u043E! \u041E\u0442\u0437\u044B\u0432 \u043E\u043F\u0443\u0431\u043B\u0438\u043A\u043E\u0432\u0430\u043D"
      }));
      await Promise.all([qc.invalidateQueries({
        queryKey: ["buyer", userId, "reviews"]
      }), qc.invalidateQueries({
        queryKey: ["products", "reviews", productId]
      })]);
      setStars(0);
    };
    $[16] = item.orderId;
    $[17] = item.productId;
    $[18] = productId;
    $[19] = qc;
    $[20] = t;
    $[21] = userId;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const submit = t8;
  let t9;
  if ($[23] !== t) {
    t9 = t("product.ratePurchased", {
      defaultValue: "\u041E\u0446\u0435\u043D\u0438\u0442\u0435 \u0442\u043E\u0432\u0430\u0440"
    });
    $[23] = t;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t9) {
    t10 = <p className="flex-1 text-center text-[16px] font-semibold leading-3 text-black">{t9}</p>;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t) {
    t11 = t("common.close", {
      defaultValue: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C"
    });
    $[27] = t;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <XIcon />;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== dismiss || $[31] !== t11) {
    t13 = <button type="button" onClick={dismiss} aria-label={t11} className="grid size-6 shrink-0 place-items-center rounded-[4px] text-[#1F1F1F] outline-none hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-ds-focus">{t12}</button>;
    $[30] = dismiss;
    $[31] = t11;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t10 || $[34] !== t13) {
    t14 = <div className="flex items-center gap-6 pl-6">{t10}{t13}</div>;
    $[33] = t10;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t) {
    t15 = t("product.ratePurchased", {
      defaultValue: "\u041E\u0446\u0435\u043D\u0438\u0442\u0435 \u0442\u043E\u0432\u0430\u0440"
    });
    $[36] = t;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setHover(0);
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== hover || $[40] !== t) {
    t17 = [1, 2, 3, 4, 5].map(n => <button key={n} type="button" onMouseEnter={() => setHover(n)} onClick={() => setStars(n)} aria-label={t("product.rateStars", {
      defaultValue: "\u041E\u0446\u0435\u043D\u043A\u0430 {{n}} \u0438\u0437 5",
      n
    })} className="grid size-9 place-items-center rounded-[6px] outline-none focus-visible:ring-2 focus-visible:ring-ds-focus"><Star32Icon className={cn("transition-colors", n <= hover ? "text-osmix-yellow" : "text-[#E2E2E2]")} /></button>);
    $[39] = hover;
    $[40] = t;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t15 || $[43] !== t17) {
    t18 = <fieldset aria-label={t15} className="m-0 flex min-w-0 justify-center gap-1 border-0 p-0" onMouseLeave={t16}>{t17}</fieldset>;
    $[42] = t15;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t14 || $[46] !== t18) {
    t19 = <div data-testid="rate-purchased-bar" className="fixed inset-x-0 bottom-[calc(67px+env(safe-area-inset-bottom))] z-40 flex flex-col gap-4 bg-white pt-4 pr-5 pb-[30px] pl-4 shadow-[0_-1px_4px_rgba(12,12,13,0.05)] sm:pl-[30px] lg:bottom-0">{t14}{t18}</div>;
    $[45] = t14;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== item.brand || $[49] !== item.image || $[50] !== item.name || $[51] !== item.orderId || $[52] !== item.productId || $[53] !== item.variant || $[54] !== stars || $[55] !== submit) {
    t20 = stars > 0 && <LeaveReviewModal key={`${item.orderId}-${item.productId}`} open={true} onOpenChange={o => {
      if (!o) {
        setStars(0);
      }
    }} subject={{
      photo: item.image ?? undefined,
      title: item.name,
      subtitle: item.brand,
      variant: item.variant ?? undefined
    }} onSubmit={submit} initialRating={stars} onUploadMedia={_temp4} />;
    $[48] = item.brand;
    $[49] = item.image;
    $[50] = item.name;
    $[51] = item.orderId;
    $[52] = item.productId;
    $[53] = item.variant;
    $[54] = stars;
    $[55] = submit;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== t19 || $[58] !== t20) {
    t21 = <>{t19}{t20}</>;
    $[57] = t19;
    $[58] = t20;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  return t21;
}
function _temp4(file) {
  return api.cabinet.uploadReviewMedia(file);
}
function _temp3() {
  return api.cabinet.pendingReviews();
}
function _temp2(s_0) {
  return s_0.user?.id ?? "guest";
}
function _temp(s) {
  return s.token;
}